/**
 * IndisponibleState — the graceful "explication indisponible" panel state (AIX-0). The server's
 * generate/regenerate NEVER throws: a missing AI key (`disabled`), a not-yet-emitted CR / quota / outage
 * / refusal (`error`) all come back tagged, and the signed report is NEVER blocked. This communicates
 * that posture in plain language (glyph + text, not color alone) and offers a retry where it makes sense.
 *
 *   - disabled  → the service isn't configured on this install (no retry — config, not transient).
 *   - error+quota / +not_emitted → a tailored line; quota/transient errors get a retry.
 */
import { Button } from "@/ui";
import { useT } from "@/app";

export interface IndisponibleStateProps {
  status: "disabled" | "error";
  reason?: string | undefined;
  /** Retry the generation (transient errors / quota). Omit to hide the retry. */
  onRetry?: (() => void) | undefined;
  retrying?: boolean;
}

function bodyKey(status: "disabled" | "error", reason?: string): string {
  if (status === "disabled") return "aix.indispo.disabled";
  if (reason === "not_emitted") return "aix.indispo.notEmitted";
  if (reason === "quota") return "aix.indispo.quota";
  return "aix.indispo.error";
}

/** A transient/quota error is retryable; a not-emitted CR or a disabled service is not. */
function isRetryable(status: "disabled" | "error", reason?: string): boolean {
  return status === "error" && reason !== "not_emitted";
}

export function IndisponibleState({ status, reason, onRetry, retrying }: IndisponibleStateProps) {
  const { t } = useT();
  const showRetry = onRetry && isRetryable(status, reason);

  return (
    <div className="nv-state" role="status" aria-live="polite" style={{ gap: "var(--nv-s-3)" }}>
      <div className="nv-state__glyph" aria-hidden style={{ color: "var(--nv-steel)" }}>
        ⌀
      </div>
      <div className="nv-state__title">{t("aix.indispo.title")}</div>
      <p className="nv-state__desc">{t(bodyKey(status, reason))}</p>
      {showRetry && (
        <Button variant="secondary" loading={retrying ?? false} onClick={onRetry}>
          {t("aix.indispo.retry")}
        </Button>
      )}
    </div>
  );
}
